import { SiteUrls } from '@tableau/shared-urls';
import { delayPromise } from '@tableau/vizportalclient-common-components';
import { ButtonType, DialogPositioningBehavior, DialogType, TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { showActionableToast } from '~commonInfra/components/ActionableMessage';
import { ButtonWithSpinner } from '~commonInfra/components/ButtonWithSpinner';
import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { ToastType } from '~commonInfra/services/ToasterService';
import { CollectionsErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Location } from '~commonInfra/utils/Location';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { Formatters,Messages } from '../../codegen/Localize';
import { L10n } from '../../commonInfra/L10n/L10n';
import { IContentInfo } from '../../spacesPlatform/models/IContentInfo';
import { cssClass } from '../../style';
import { CollectionsResourceQuery } from '../ResourceQuery/CollectionsResourceQuery';
import { COLLECTION_NAME_MAX_LENGTH } from './CollectionHeaderEditableName';
import { CollectionsDialogStyles } from './CollectionsDialogStyles';
import { CreateCollectionClickedSourceOptions,sendCreateCollectionClickedTelemetry } from './CollectionsTelemetry';
import { ICollectionInfo } from './ICollectionInfo';

export const CreateCollectionDialogTestId = 'create-collection-dialog';
export const CreateCollectionDialogDelay = 500;

export namespace CreateCollectionDialog {
  export interface ContentsProps {
    // **this cannot be named `close` otherwise it gets overriden by the ReactModalService**
    readonly closeDialog: (createdCollection?: ICollectionInfo) => void;
    readonly siteUrls: SiteUrls;
    readonly spaceInfo: SpaceInfo;
    readonly isFirstCollection: boolean;
    readonly itemsToPossiblyAdd?: IContentInfo[];
  }

  export interface ContentsState {
    readonly value: string;
    readonly message: string;
    readonly isBusy: boolean;
    readonly isErrored: boolean;
  }

  export const InitialName = '';
  const CreateCollectionInputStyle = cssClass('create-collection-dialog', 'collection-title-input', {
    width: '100%'
  });

  export class Contents extends React.PureComponent<ContentsProps, ContentsState> {
    constructor(props: ContentsProps) {
      super(props);
      this.state = {
        value: InitialName,
        message: this.getMessageForNameTextInput(InitialName),
        isBusy: false,
        isErrored: false,
      };
    }

    public render(): JSX.Element {
      return (
        <>
          <TextFieldWidget
            testId='collection-name'
            label={Messages.createCollectionInputLabel()}
            maxLength={COLLECTION_NAME_MAX_LENGTH}
            onChange={this.onNameChange}
            onKeyDown={this.handleKeyDown}
            value={this.state.value}
            message={this.state.message}
            valid={this.state.isErrored ? false : undefined} // valid=undefined so textarea won't have green valid styling
            style={{width: '100%'}}
            className={CreateCollectionInputStyle}
          />
          <div className={CollectionsDialogStyles.CreateCancelButtons}>
            <ButtonWithSpinner
              title={Messages.cancel()}
              buttonType={ButtonType.Outline}
              clicked={
                // if first collection, cancel closes dialog. Otherwise, go back to `MangageItemsInCollectionsDialog`
                // by calling `closeDialog` with non-null value
                this.props.isFirstCollection ?
                  () => this.props.closeDialog(null) :
                  () => this.props.closeDialog({} as ICollectionInfo)
              }
              testId={'cancel-create-collection'}
              disabled={this.state.isBusy}
            />
            <span className="tb-margin-left-wide">
              <ButtonWithSpinner
                title={Messages.create()}
                buttonType={ButtonType.Primary}
                testId={'create-collection'}
                disabled={this.isButtonDisabled()}
                clicked={this.createCollection}
                showSpinner={this.state.isBusy}
              />
            </span>
          </div>
        </>
      );
    }

    private isButtonDisabled = (): boolean => {
      return this.state.isErrored || this.state.isBusy || this.state.value.trim().length === 0 || this.state.value.length > COLLECTION_NAME_MAX_LENGTH;
    };

    private createCollection = async (): Promise<void> => {
      this.setState({isBusy: true});
      try {
        sendCreateCollectionClickedTelemetry(this.props.spaceInfo.spaceType, CreateCollectionClickedSourceOptions.DialogCreateButton);
        const collectionInfo: ICollectionInfo = await (new CollectionsResourceQuery()).create(this.state.value);

        await delayPromise(CreateCollectionDialogDelay); // TFS 1226348: wait for CRASS to update before closing dialog and fetching collections

        this.setState({isBusy: false});

        if (this.props.itemsToPossiblyAdd && this.props.itemsToPossiblyAdd.length > 0) {
          this.props.closeDialog(collectionInfo);
        } else {
          this.props.closeDialog(null);
        }

        showActionableToast({
          testId: 'collection-creation-success',
          message: Messages.createCollectionSuccess({ NEW_ITEM_NAME: L10n.elideIfLong(collectionInfo.name) }),
          actionMessage: Messages.goToCollection(),
          action: () => { Location.instance.setUrl(this.props.siteUrls.collectionUrl(collectionInfo.luid)); }
        }, ToastType.Success);
      } catch (e) {
        const errorCode = e.data.errorCode;
        let errorText = Messages.errorUnknown();
        if (errorCode === CollectionsErrorCode.INVALID_NAME_NULL || errorCode === CollectionsErrorCode.INVALID_NAME_WHITESPACE) {
          errorText = Messages.createCollectionNameWhitespaceError();
        }
        else if (errorCode === CollectionsErrorCode.INVALID_NAME_TOO_LONG) {
          errorText = Messages.createCollectionNameTooLongError();
        }

        this.setState({
          isErrored: true,
          message: errorText,
          isBusy: false,
        });
        throw e;
      }
    };

    private onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      this.setState({value: value});
      this.setState({message: this.getMessageForNameTextInput(value)});
    };

    private getMessageForNameTextInput = (value: string): string => {
      if (!value || value === InitialName || value.trim().length !== 0) {
        this.state && this.state.isErrored && this.setState({isErrored: undefined});
        const remainingChars = COLLECTION_NAME_MAX_LENGTH - value.length;
        return Messages.remainingCharacters({
          COUNT: remainingChars,
          COUNT_LOC: Formatters.formatCount(remainingChars)
        });
      } else  {
        !this.state.isErrored && this.setState({isErrored: true});
        return Messages.createCollectionNameWhitespaceError();
      }
    };

    private handleKeyDown = (e: React.KeyboardEvent) => {
      if(e.key === 'Enter' && !this.isButtonDisabled()) {
        this.createCollection();
      }
    };
  }

  export function open(urls: SiteUrls, spaceInfo: SpaceInfo, isFirstCollection: boolean, itemsToPossiblyAdd?: IContentInfo[]): Promise<any> {
    const closeDialog = (createdCollection?: ICollectionInfo) => modalInstance?.close(createdCollection);

    const modalInstance = ReactModalService.open({
      title: Messages.newCollection(),
      testId: CreateCollectionDialogTestId,
      type: DialogType.NormalWithTitleBar,
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogWidth: CollectionsDialogStyles.ADD_TO_DIALOG_WIDTH,
      dialogBodyStyle: { maxWidth: CollectionsDialogStyles.ADD_TO_DIALOG_WIDTH + 'px' },
      dialogContent: <Contents closeDialog={closeDialog} siteUrls={urls} spaceInfo={spaceInfo} isFirstCollection={isFirstCollection} itemsToPossiblyAdd={itemsToPossiblyAdd}/>,
    });
    return modalInstance.result;
  }
}
